import { useState } from 'react';
import { Copy, ExternalLink, ShieldCheck, ShieldOff } from 'lucide-react';
import { toast } from 'sonner';
import { useI18n } from '@/i18n/I18nProvider';
import { Badge, Button, FieldError, Input, Label, Skeleton } from '@/shared/ui';
import { securityError, useTwoFactorDisable, useTwoFactorEnable, useTwoFactorSetup, useTwoFactorStatus, type TwoFactorSetup } from './account.api';

/** Setting up (QR + confirmation code) and turning off the authenticator-app second factor. */
export function TwoFactorPanel({ onEnabled }: { onEnabled?: () => void }) {
  const { t } = useI18n();
  const status = useTwoFactorStatus();
  const setup = useTwoFactorSetup();
  const enable = useTwoFactorEnable();
  const disable = useTwoFactorDisable();
  const [pending, setPending] = useState<TwoFactorSetup | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  if (status.isLoading) return <Skeleton className="h-24" />;
  const enabled = status.data?.enabled ?? false;
  const onlyDigits = (v: string) => v.replace(/\D/g, '').slice(0, 6);

  const begin = () => {
    setError(null);
    setup.mutate(undefined, { onSuccess: setPending, onError: (e) => setError(t(`acct.err.${securityError(e)}`)) });
  };
  const confirm = () => {
    if (code.length !== 6) return setError(t('acct.err.codeLength'));
    setError(null);
    enable.mutate(code, {
      onSuccess: () => {
        toast.success(t('acct.twoFactorOn'));
        setPending(null);
        setCode('');
        onEnabled?.();
      },
      onError: (e) => setError(t(`acct.err.${securityError(e)}`)),
    });
  };
  const turnOff = () => {
    if (code.length !== 6) return setError(t('acct.err.codeLength'));
    setError(null);
    disable.mutate(code, {
      onSuccess: () => {
        toast.success(t('acct.twoFactorOff'));
        setCode('');
      },
      onError: (e) => setError(t(`acct.err.${securityError(e)}`)),
    });
  };

  if (pending) {
    return (
      <div className="space-y-3">
        <p className="text-[13px]">{t('acct.scan')}</p>
        <div className="flex flex-wrap items-center gap-4">
          <img src={pending.qrDataUrl} alt={t('acct.qrAlt')} className="size-40 rounded-xl border border-border bg-white p-1.5" />
          <div className="min-w-0 space-y-2 text-xs text-muted-foreground">
            <p>{t('acct.orManual')}</p>
            <div className="flex items-center gap-2">
              <code className="select-all break-all rounded-lg bg-muted px-2 py-1 font-mono text-[12px] font-bold text-foreground">{pending.secret}</code>
              <Button variant="ghost" size="icon-sm" aria-label={t('acct.copySecret')} onClick={() => void navigator.clipboard.writeText(pending.secret).then(() => toast.success(t('acct.copied')))}>
                <Copy />
              </Button>
            </div>
            <a href={pending.otpauthUrl} className="inline-flex items-center gap-1 font-semibold text-primary hover:underline">
              <ExternalLink className="size-3.5" aria-hidden />
              {t('acct.openApp')}
            </a>
          </div>
        </div>
        <form
          className="space-y-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            confirm();
          }}
        >
          <Label htmlFor="acct-code">{t('acct.code')}</Label>
          <div className="flex items-center gap-2">
            <Input id="acct-code" className="w-36 text-center font-mono tracking-[0.3em]" inputMode="numeric" autoComplete="one-time-code" placeholder="000000" value={code} onChange={(e) => setCode(onlyDigits(e.target.value))} autoFocus />
            <Button type="submit" variant="primary" loading={enable.isPending}>
              {t('acct.confirm')}
            </Button>
            <Button type="button" variant="ghost" onClick={() => (setPending(null), setCode(''), setError(null))}>
              {t('common.cancel')}
            </Button>
          </div>
          <FieldError>{error}</FieldError>
        </form>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2.5">
        {enabled ? <ShieldCheck className="size-5 text-success" aria-hidden /> : <ShieldOff className="size-5 text-muted-foreground" aria-hidden />}
        <div className="flex-1">
          <p className="text-[13px] font-semibold">{enabled ? t('acct.twoFactorOn') : t('acct.twoFactorOffState')}</p>
          <p className="text-xs text-muted-foreground">{enabled ? t('acct.twoFactorOnBody') : t('acct.twoFactorOffBody')}</p>
        </div>
        <Badge tone={enabled ? 'success' : 'neutral'}>{enabled ? t('acct.on') : t('acct.off')}</Badge>
      </div>
      {enabled ? (
        <form
          className="space-y-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            turnOff();
          }}
        >
          <Label htmlFor="acct-code-off">{t('acct.codeToDisable')}</Label>
          <div className="flex items-center gap-2">
            <Input id="acct-code-off" className="w-36 text-center font-mono tracking-[0.3em]" inputMode="numeric" autoComplete="one-time-code" placeholder="000000" value={code} onChange={(e) => setCode(onlyDigits(e.target.value))} />
            <Button type="submit" variant="secondary" loading={disable.isPending}>
              {t('acct.disable')}
            </Button>
          </div>
          <FieldError>{error}</FieldError>
        </form>
      ) : (
        <>
          <Button variant="primary" loading={setup.isPending} onClick={begin}>
            {t('acct.enable')}
          </Button>
          <FieldError>{error}</FieldError>
        </>
      )}
    </div>
  );
}
